import React, { useEffect, useMemo, useState } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"
import IconButton from "@react/shared/ui/IconButton"
import Input, { inputVariants } from "@react/shared/ui/Input"
import { t, fmtHours, clock } from "../helpers/format"
import { spanHours } from "../helpers/dates"
import GpsPositions from "./GpsPositions"

const KINDS = ["work", "travel", "bad_weather"]
const KIND_ICONS = { work: "helmet-safety", travel: "car", bad_weather: "cloud-rain", activity: "toolbox" }
// Off-site activities share the project select, told apart by their prefix.
const ACTIVITY = "a"
const activityValue = id => `${ACTIVITY}${id}`
const activityId = value => (value?.startsWith(ACTIVITY) ? value.slice(ACTIVITY.length) : null)

const initialDraft = entry => ({
  project_id:      entry.category_id ? activityValue(entry.category_id) : entry.project_id ? String(entry.project_id) : "",
  project_task_id: entry.task_id ? String(entry.task_id) : "",
  kind:            entry.kind || "work",
  started_at:      clock(entry.started_at),
  ended_at:        clock(entry.ended_at),
  break_minutes:   entry.break_minutes ?? 0,
  hours:           entry.hours ? String(entry.hours) : "",
  comment:         entry.comment || "",
})

const Field = ({ label, className, children }) => (
  <label className={clsx("flex flex-col gap-1 m-0 min-w-0", className)}>
    <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
    {children}
  </label>
)

const SourceTag = ({ entry }) => {
  if (entry.draft) return <Tag icon="plus" tone="brand">{t("entry.new")}</Tag>
  if (entry.planned) return <Tag icon="calendar-days" tone="slate">{t("entry.planned")}</Tag>

  if (entry.source === "clock") return <Tag icon="stopwatch" tone="brand">{entry.running ? t("entry.running") : t("entry.clocked")}</Tag>
  return <Tag icon="pen" tone="blue">{entry.entered_by ? t("entry.entered_by", { name: entry.entered_by }) : t("entry.manual")}</Tag>
}

const TAG_TONES = {
  slate: "text-slate-500",
  brand: "text-brand-600",
  blue:  "text-blue-600",
}

const Tag = ({ icon, tone, children }) => (
  <span className={clsx("inline-flex items-center gap-1 text-[11px] font-medium", TAG_TONES[tone])}>
    <FontAwesomeIcon icon={icon} className="text-[10px]" />
    {children}
  </span>
)

const EntryRow = ({ entry, projects, settings, editable, busy, onSave, onDelete }) => {
  const [draft, setDraft] = useState(() => initialDraft(entry))
  const initial   = useMemo(() => initialDraft(entry), [entry])
  const signature = JSON.stringify(initial)
  // Reset only when the saved slot itself changed, not on every refresh of the day.
  useEffect(() => setDraft(initial), [signature])

  const locked  = !editable
  const dirty   = JSON.stringify(draft) !== signature
  const virtual = entry.planned || entry.draft
  const set     = (key, value) => setDraft(current => ({ ...current, [key]: value }))

  const projectOptions = useMemo(() => {
    const list = (projects || []).map(project => ({ id: String(project.id), name: project.name, color: project.color, tasks: project.tasks || [] }))
    if (entry.project_id && !list.some(project => project.id === String(entry.project_id))) {
      list.unshift({ id: String(entry.project_id), name: entry.project_name, color: entry.project_color, tasks: [] })
    }
    return list
  }, [projects, entry])

  const activityOptions = useMemo(() => {
    const list = (settings?.activities || []).map(activity => ({ id: activityValue(activity.id), name: activity.name, color: activity.color }))
    if (entry.category_id && !list.some(activity => activity.id === activityValue(entry.category_id))) {
      list.unshift({ id: activityValue(entry.category_id), name: entry.project_name, color: entry.project_color })
    }
    return list
  }, [settings, entry])

  const categoryId = activityId(draft.project_id)
  const kind     = categoryId ? "activity" : draft.kind === "activity" ? "work" : draft.kind
  const project  = projectOptions.find(option => option.id === draft.project_id)
  const activity = activityOptions.find(option => option.id === draft.project_id)
  const tasks = useMemo(() => {
    const list = (project?.tasks || []).map(task => ({ id: String(task.id), title: task.title }))
    if (entry.task_id && String(entry.project_id) === draft.project_id && !list.some(task => task.id === String(entry.task_id))) {
      list.unshift({ id: String(entry.task_id), title: entry.task_title })
    }
    return list
  }, [project, entry, draft.project_id])

  const timed    = !!(draft.started_at || draft.ended_at)
  const computed = timed ? spanHours(draft.started_at, draft.ended_at, draft.break_minutes) : Number(draft.hours) || 0
  const badSpan  = draft.started_at && draft.ended_at && draft.ended_at === draft.started_at
  const needsProject = kind === "work" && !draft.project_id
  const canSave  = !locked && !busy && !badSpan && !needsProject && (timed ? !!draft.started_at : Number(draft.hours) > 0)
  const color    = project?.color || activity?.color || entry.project_color || "#CBD5E1"

  const save = () => {
    const sameActivity = categoryId && String(entry.category_id) === categoryId
    const attributes = {
      project_id:      categoryId ? "" : draft.project_id,
      project_task_id: categoryId ? "" : draft.project_task_id,
      category_id:     categoryId || "",
      absence_id:      sameActivity ? entry.absence_id || "" : "",
      kind:            kind,
      break_minutes:   Number(draft.break_minutes) || 0,
      comment:         draft.comment,
    }
    if (timed) {
      attributes.started_at = draft.started_at
      attributes.ended_at   = draft.ended_at
    } else {
      attributes.started_at = ""
      attributes.ended_at   = ""
      attributes.hours      = Number(draft.hours) || 0
    }
    onSave(attributes).catch(() => {})
  }

  return (
    <div
      className = {clsx(
        "relative rounded-lg border pl-4 pr-3 py-3 transition-colors",
        virtual ? "border-dashed border-slate-300 bg-slate-50/60" : "border-slate-200 bg-white",
        entry.outside_geofence && "!border-amber-300"
      )}
    >
      <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full" style={{ backgroundColor: color }} />

      <div className="flex items-center gap-2 mb-2">
        <SourceTag entry={entry} />
        {entry.outside_geofence && (
          <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-full bg-amber-50 ring-1 ring-inset ring-amber-200 text-[10.5px] font-semibold text-amber-700">
            <FontAwesomeIcon icon="location-dot" className="text-[9px]" />
            {t("gps.outside")}
          </span>
        )}
        <span className="ml-auto text-[15px] font-bold tabular-nums text-slate-900">
          {computed != null ? fmtHours(computed) : "–"}
        </span>
        {!locked && !entry.planned && (
          <IconButton tone="danger" size="sm" disabled={busy} onClick={onDelete} title={t("entry.delete")} aria-label={t("entry.delete")}>
            <FontAwesomeIcon icon="trash-can" />
          </IconButton>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field label={t("entry.project")}>
          <select
            value     = {draft.project_id}
            disabled  = {locked}
            onChange  = {e => setDraft(current => ({ ...current, project_id: e.target.value, project_task_id: "" }))}
            className = {clsx(inputVariants({ size: "sm" }), "truncate")}
          >
            <option value="">{kind === "work" ? t("entry.choose_project") : t("entry.no_project")}</option>
            {activityOptions.length ? (
              <>
                <optgroup label={t("entry.projects")}>
                  {projectOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                </optgroup>
                <optgroup label={t("entry.off_site")}>
                  {activityOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                </optgroup>
              </>
            ) : projectOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select>
        </Field>
        <Field label={t("entry.task")}>
          <select
            value     = {draft.project_task_id}
            disabled  = {locked || !tasks.length}
            onChange  = {e => set("project_task_id", e.target.value)}
            className = {clsx(inputVariants({ size: "sm" }), "truncate")}
          >
            <option value="">{tasks.length ? t("entry.no_task") : t("entry.no_tasks")}</option>
            {tasks.map(task => <option key={task.id} value={task.id}>{task.title}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-2 grid grid-cols-[1.3fr_1fr_1fr_0.8fr] gap-2">
        <Field label={t("entry.kind")}>
          <div className="relative">
            <FontAwesomeIcon icon={KIND_ICONS[kind]} className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none" />
            <select
              value     = {kind}
              disabled  = {locked || !!categoryId}
              onChange  = {e => set("kind", e.target.value)}
              className = {clsx(inputVariants({ size: "sm" }), "!pl-6")}
            >
              {(categoryId ? ["activity"] : KINDS).map(value => <option key={value} value={value}>{I18n.t(`project_hours.kinds.${value}`)}</option>)}
            </select>
          </div>
        </Field>
        <Field label={t("entry.start")}>
          <Input type="time" size="sm" value={draft.started_at} disabled={locked} onChange={e => set("started_at", e.target.value)} className="tabular-nums" />
        </Field>
        <Field label={t("entry.end")}>
          <Input
            type      = "time"
            size      = "sm"
            value     = {draft.ended_at}
            disabled  = {locked}
            onChange  = {e => set("ended_at", e.target.value)}
            className = {clsx("tabular-nums", badSpan && "!border-rose-400")}
            placeholder = {entry.running ? t("entry.running") : ""}
          />
        </Field>
        {timed ? (
          <Field label={t("entry.break")}>
            <Input type="number" min="0" step="5" size="sm" align="right" value={draft.break_minutes} disabled={locked} onChange={e => set("break_minutes", e.target.value)} />
          </Field>
        ) : (
          <Field label={t("entry.hours")}>
            <Input type="number" min="0" max="24" step="0.25" size="sm" align="right" value={draft.hours} disabled={locked} onChange={e => set("hours", e.target.value)} />
          </Field>
        )}
      </div>

      <div className="mt-2">
        <Input
          size        = "sm"
          value       = {draft.comment}
          disabled    = {locked}
          placeholder = {t("entry.comment")}
          onChange    = {e => set("comment", e.target.value)}
        />
      </div>

      {(badSpan || (needsProject && (dirty || virtual))) && (
        <div className="mt-2 text-[11.5px] text-rose-600">
          {badSpan ? t("entry.bad_span") : t("entry.project_required")}
        </div>
      )}

      <GpsPositions entry={entry} radius={settings?.geofence_meters} />

      {!locked && (dirty || virtual) && (
        <div className="mt-3 flex items-center justify-between animate-[hb-rise_140ms_ease-out]">
          <Button
            variant = "ghost"
            size    = "sm"
            onClick = {() => (entry.draft ? onDelete() : setDraft(initial))}
            disabled = {busy || (!dirty && !entry.draft)}
          >
            {t("actions.cancel")}
          </Button>
          <Button variant="primary" size="sm" disabled={!canSave} onClick={save}>
            <FontAwesomeIcon icon={busy ? "circle-notch" : "check"} spin={busy} />
            {entry.planned && !dirty ? t("entry.confirm") : entry.draft ? t("entry.add") : t("entry.save")}
          </Button>
        </div>
      )}
    </div>
  )
}

export default EntryRow
